import CategoryPill from './CategoryPill';
import FilePill from './FilePill';
import PersonaPill from './PersonaPill';
import PrivateDistributionListPill from './PrivateDistributionListPill';
import { removePersonAriaLabelNext } from './SearchBoxPillWell.locstring.json';
import SuggestionSegment from './SuggestionSegment';
import onRemoveGuidedFormulationPill from '../actions/onRemoveGuidedFormulationPill';
import onRemoveSuggestionPill from '../actions/onRemoveSuggestionPill';
import updateSelectedPillIndex from '../actions/updateSelectedPillIndex';
import getScenarioStore from '../store/getScenarioStore';
import { createDefaultSearchStore } from '../store/store';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { getCategoryColorsForCategory, getMasterCategoryList } from 'owa-categories';
import { ControlIcons } from 'owa-control-icons';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import loc, { format } from 'owa-localize';
import { SearchScenarioId } from 'owa-search-next-common';
import * as React from 'react';
import {
    onLeftArrowPressedSearchInput,
    onRightArrowPressedSearchInput,
} from '../actions/onKeyDownSearchInput';
import {
    CategorySearchSuggestion,
    FileSuggestion,
    PeopleSuggestion,
    PrivateDistributionListSuggestion,
    SuggestionKind,
} from 'owa-search-service';

/* tslint:disable:jsx-no-lambda WI:47653 */

const styles = require('./SearchBoxPillWell.scss');
const classNames = require('classnames');

export interface SearchBoxPillWellProps {
    focusSearchInput: () => void;
    onRemovePill: (pillId: string) => void;
    onRemoveSegment: (segmentKey: string) => void;
    scenarioId: SearchScenarioId;
}

@observer
export default class SearchBoxPillWell extends React.Component<SearchBoxPillWellProps, any> {
    private searchBoxPillWell: HTMLDivElement;

    render() {
        return (
            <div
                className={styles.searchBoxPillWell}
                tabIndex={-1}
                onBlur={this.onBlur}
                onKeyDown={this.onKeyDown}
                ref={ref => (this.searchBoxPillWell ? null : (this.searchBoxPillWell = ref))}>
                {this.renderPills()}
                {this.renderSegments()}
            </div>
        );
    }

    private onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        evt.preventDefault();
        evt.stopPropagation();
        const evtKeyCode = evt.keyCode;

        const scenarioId = this.props.scenarioId;
        const {
            suggestionPills,
            suggestionSegments,
            selectedPillIndex,
            suggestionPillIds,
        } = getScenarioStore(scenarioId);

        switch (evtKeyCode) {
            case KeyboardCharCodes.Backspace:
            case KeyboardCharCodes.Delete: {
                /**
                 * There can only be suggestionPills (non-GF) OR suggestionSegments
                 * (GF) in the search box. So if we have any suggestionPills
                 * it means we're deleting a suggestionPill, and if we have any
                 * suggestionSegments it means we're deleting a suggestionSegment.
                 */
                if (suggestionPills.size > 0) {
                    onRemoveSuggestionPill(
                        suggestionPillIds[selectedPillIndex],
                        scenarioId,
                        'KeyboardFocusedPill'
                    );
                } else if (suggestionSegments.size > 0) {
                    const selectedSegmentKey = [...suggestionSegments.keys()][selectedPillIndex];
                    onRemoveGuidedFormulationPill(selectedSegmentKey, scenarioId);
                }

                // If there are no more pills, re-focus search input.
                if (suggestionPills.size === 0 && suggestionSegments.size === 0) {
                    this.props.focusSearchInput();
                }
                break;
            }
            case KeyboardCharCodes.Right_arrow: {
                const pillsLength = suggestionPills.size || suggestionSegments.size;

                /**
                 * If user already has right-most pill selected, then kick
                 * focus back to SearchInput.
                 */
                if (selectedPillIndex === pillsLength - 1) {
                    this.props.focusSearchInput();
                    return;
                }

                onRightArrowPressedSearchInput(scenarioId);
                break;
            }
            case KeyboardCharCodes.Left_arrow: {
                onLeftArrowPressedSearchInput(0 /* cursorPosition */, scenarioId);
                break;
            }
            default:
                break;
        }
    };

    private getPersonaPillProperties(pillId: string, pillIndex: number) {
        const isPillSelected =
            getScenarioStore(this.props.scenarioId).selectedPillIndex === pillIndex;

        const personaPillContainerClassNames = classNames(
            styles.personaPillContainer,
            isPillSelected && styles.selected
        );
        return { personaPillContainerClassNames, isPillSelected };
    }

    private renderPersonaPillIconButton(pillId: string, personaDisplayName: string) {
        return (
            <IconButton
                className={styles.removePersonaIcon}
                tabIndex={-1}
                iconProps={{
                    iconName: ControlIcons.Cancel,
                    styles: {
                        root: styles.cancelIcon,
                    },
                }}
                ariaLabel={format(loc(removePersonAriaLabelNext), personaDisplayName)}
                onClick={ev => {
                    ev.stopPropagation();
                    this.props.onRemovePill(pillId);
                }}
            />
        );
    }

    private renderPersonaPill(
        pillId: string,
        persona: PeopleSuggestion,
        pillIndex: number
    ): JSX.Element {
        const { personaPillContainerClassNames, isPillSelected } = this.getPersonaPillProperties(
            pillId,
            pillIndex
        );

        return (
            <div key={pillId} className={personaPillContainerClassNames}>
                <PersonaPill
                    name={persona.DisplayName}
                    emailAddress={persona.EmailAddresses[0]}
                    mailBoxType={persona.MailboxType}
                    isSelected={isPillSelected}
                    tabIndex={-1}
                />
                {this.renderPersonaPillIconButton(pillId, persona.DisplayName)}
            </div>
        );
    }

    private renderPrivateDistributionListPill(
        pillId: string,
        privateDistributionList: PrivateDistributionListSuggestion,
        pillIndex: number
    ): JSX.Element {
        const { personaPillContainerClassNames, isPillSelected } = this.getPersonaPillProperties(
            pillId,
            pillIndex
        );

        return (
            <div key={pillId} className={personaPillContainerClassNames}>
                <PrivateDistributionListPill
                    name={privateDistributionList.DisplayName}
                    personas={privateDistributionList.Members}
                    isSelected={isPillSelected}
                />
                {this.renderPersonaPillIconButton(pillId, privateDistributionList.DisplayName)}
            </div>
        );
    }

    private renderCategoryPilll(
        pillId: string,
        categorySuggestion: CategorySearchSuggestion,
        pillIndex: number
    ): JSX.Element {
        const categoryName = categorySuggestion.Name;
        const categoryColor = getCategoryColorsForCategory(categoryName, getMasterCategoryList());
        const categoryPillStyle = {
            backgroundColor: categoryColor.primaryColor,
            borderColor: categoryColor.secondaryColor,
            color: categoryColor.textColor,
        };
        const iconStyles = { color: categoryColor.iconColor };
        const iconStylesOnHover = { backgroundColor: categoryColor.secondaryColor };
        const isPillSelected =
            getScenarioStore(this.props.scenarioId).selectedPillIndex === pillIndex;

        return (
            <div key={pillId} className={styles.pillContainerCommon} style={categoryPillStyle}>
                <CategoryPill
                    categoryName={categoryName}
                    isSelected={isPillSelected}
                    categoryList={getMasterCategoryList()}
                />
                <Icon
                    styles={{
                        root: [
                            iconStyles,
                            styles.removeIconCommon,
                            styles.cancelIcon,
                            {
                                selectors: {
                                    '&:hover': iconStylesOnHover,
                                },
                            },
                        ],
                    }}
                    iconName={ControlIcons.Cancel}
                    onClick={ev => {
                        ev.stopPropagation();
                        this.props.onRemovePill(pillId);
                    }}
                />
            </div>
        );
    }

    private renderFilePill(
        pillId: string,
        fileSuggestion: FileSuggestion,
        pillIndex: number
    ): JSX.Element {
        const isPillSelected =
            getScenarioStore(this.props.scenarioId).selectedPillIndex === pillIndex;

        const filePillContainerClassNames = classNames(
            styles.filePillContainer,
            isPillSelected && styles.selected
        );

        return (
            <div key={pillId} className={filePillContainerClassNames}>
                <FilePill fileName={fileSuggestion.Name} isSelected={isPillSelected} />
                <IconButton
                    className={styles.removeFileIcon}
                    iconProps={{
                        iconName: ControlIcons.Cancel,
                        styles: {
                            root: styles.cancelIcon,
                        },
                    }}
                    onClick={ev => {
                        ev.stopPropagation();
                        this.props.onRemovePill(pillId);
                    }}
                />
            </div>
        );
    }

    private renderPills(): JSX.Element[] {
        const store = getScenarioStore(this.props.scenarioId);

        const pills: JSX.Element[] = [];
        const length = store.suggestionPillIds.length;

        for (let i = 0; i < length; i++) {
            const pillId = store.suggestionPillIds[i];
            const suggestion = store.suggestionPills.get(pillId);

            if (suggestion.kind == SuggestionKind.People) {
                pills.push(this.renderPersonaPill(pillId, suggestion, i /* pillIndex */));
            } else if (suggestion.kind == SuggestionKind.PrivateDistributionList) {
                pills.push(
                    this.renderPrivateDistributionListPill(pillId, suggestion, i /* pillIndex */)
                );
            } else if (suggestion.kind == SuggestionKind.Category) {
                pills.push(this.renderCategoryPilll(pillId, suggestion, i /* pillIndex */));
            } else if (suggestion.kind == SuggestionKind.Files) {
                pills.push(this.renderFilePill(pillId, suggestion, i /* pillIndex */));
            }
        }
        return pills;
    }

    private renderSegments(): JSX.Element[] {
        const store = getScenarioStore(this.props.scenarioId);

        const segments: JSX.Element[] = [];
        const suggestionSegments = store.suggestionSegments;
        const suggestionSegmentsLength = suggestionSegments.size;

        for (let i = 0; i < suggestionSegmentsLength; i++) {
            const key = [...suggestionSegments.keys()][i];
            const isSegmentSelected = store.selectedPillIndex === i;

            segments.push(
                <SuggestionSegment
                    isSelected={isSegmentSelected}
                    onRemoveSegment={this.props.onRemoveSegment}
                    scenario="SuggestionPill"
                    segment={suggestionSegments.get(key)}
                />
            );
        }

        return segments;
    }

    private onBlur = (evt: React.FocusEvent<EventTarget>) => {
        let relatedTargetElement = evt.relatedTarget as HTMLElement;

        // Fallback for IE, which doesn't always set relatedTarget.
        if (!relatedTargetElement) {
            relatedTargetElement = document.activeElement as HTMLElement;
        }

        /**
         * A way of determining if element that caused JavaScript event is from
         * within the SearchBoxPillWell component.
         */
        const isTargetElementWithinSearchBoxPillWell = this.searchBoxPillWell.contains(
            relatedTargetElement as Node
        );

        // Respond to blur only if blurring outside of component.
        if (!isTargetElementWithinSearchBoxPillWell) {
            updateSelectedPillIndex(
                this.props.scenarioId,
                createDefaultSearchStore().selectedPillIndex
            );
        }
    };
}
